import { useState } from "react";
import { useVitalsStore } from "../../stores/vitals";
import { VitalGauge } from "./VitalGauge";
import { VitalSparkline } from "./VitalSparkline";
import { SectionHeatMap, type MetricView } from "./SectionHeatMap";
import { ReadinessRing } from "./ReadinessRing";
import { cn } from "../../lib/cn";

interface VitalsDashboardProps {
  className?: string;
}

const HEATMAP_VIEWS: { id: MetricView; label: string }[] = [
  { id: "words", label: "Words" },
  { id: "citations", label: "Cites" },
  { id: "completeness", label: "Progress" },
];

export function VitalsDashboard({ className }: VitalsDashboardProps) {
  const metrics = useVitalsStore((s) => s.metrics);
  const history = useVitalsStore((s) => s.history);
  const [heatmapView, setHeatmapView] = useState<MetricView>("words");

  if (!metrics) {
    return (
      <div className={cn("flex items-center justify-center py-6 text-[11px] text-[var(--text-muted)]", className)}>
        Start writing to see document vitals.
      </div>
    );
  }

  const wordHistory = history.map((h) => h.wordCount);
  const readabilityHistory = history.map((h) => h.readabilityScore);

  return (
    <div className={cn("flex gap-4 p-3 overflow-x-auto", className)}>
      {/* Readiness ring */}
      <div className="shrink-0 flex items-center">
        <ReadinessRing score={metrics.submissionReadiness} />
      </div>

      {/* Core gauges */}
      <div className="grid grid-cols-3 gap-2 shrink-0">
        <VitalGauge
          label="Words"
          value={metrics.wordCount}
          max={Math.max(metrics.wordCount * 1.5, 1000)}
          showGauge={false}
          format={(n) => Math.round(n).toLocaleString()}
        />
        <VitalGauge
          label="Readability"
          value={metrics.readabilityScore}
          max={100}
          format={(n) => `${Math.round(n)}`}
        />
        <VitalGauge
          label="Passive"
          value={metrics.passiveVoicePercent}
          max={100}
          format={(n) => `${Math.round(n)}%`}
          thresholds={[
            { value: 0.15, color: "var(--color-signal-green)" },
            { value: 0.30, color: "var(--color-ember-orange)" },
            { value: 1.0, color: "var(--color-soft-red)" },
          ]}
        />
        <VitalGauge
          label="Citations"
          value={metrics.citationCount}
          max={Math.max(metrics.citationCount * 2, 10)}
          showGauge={false}
          format={(n) => `${Math.round(n)}`}
        />
        <VitalGauge
          label="Sections"
          value={metrics.sectionCount}
          max={Math.max(metrics.sectionCount * 2, 6)}
          showGauge={false}
          format={(n) => `${Math.round(n)}`}
        />
        <VitalGauge
          label="Avg Sent"
          value={metrics.avgSentenceLength}
          max={40}
          format={(n) => `${n.toFixed(0)} w`}
        />
      </div>

      {/* Sparklines (show if history is available) */}
      {wordHistory.length >= 2 && (
        <div className="flex flex-col gap-1.5 shrink-0 min-w-[160px]">
          <VitalSparkline
            label="Word Count"
            data={wordHistory}
            color="var(--color-lever-orange)"
          />
          <VitalSparkline
            label="Readability"
            data={readabilityHistory}
            color="var(--color-cool-azure)"
          />
        </div>
      )}

      {/* Section heat map */}
      {metrics.sectionCount > 0 && (
        <div className="shrink-0">
          <div className="flex items-center gap-1 mb-1.5">
            {HEATMAP_VIEWS.map((v) => (
              <button
                key={v.id}
                onClick={() => setHeatmapView(v.id)}
                className={cn(
                  "px-1.5 py-0.5 rounded-[var(--radius-sm)] text-[9px] font-medium",
                  "transition-colors duration-[var(--duration-fast)]",
                  heatmapView === v.id
                    ? "bg-[var(--color-lever-orange)]/10 text-[var(--color-lever-orange)]"
                    : "text-[var(--text-muted)] hover:text-[var(--text-secondary)]",
                )}
              >
                {v.label}
              </button>
            ))}
          </div>
          <SectionHeatMap
            sectionWordCounts={metrics.sectionWordCounts}
            citationsPerSection={metrics.citationsPerSection}
            sectionCompleteness={metrics.sectionCompleteness}
            view={heatmapView}
          />
        </div>
      )}
    </div>
  );
}

VitalsDashboard.displayName = "VitalsDashboard";
